/**
 * The dashboard widget catalogue.
 *
 * A widget names a block of the dashboard and the permission that block's
 * data demands. It carries no React and no copy: packages/web holds one
 * component per key, and the cross-registry test there fails on a key
 * that has no component.
 *
 * Every widget listed here is backed by data an endpoint actually
 * returns — the four original queries (`/clients/statistics`,
 * `/articles/statistics`, `/articles/low-stock`,
 * `/reports/dashboard-stats`) and `/dashboard/summary`, which serves the
 * rest block by block. A role's station that no endpoint serves gets no
 * widget rather than an invented figure.
 *
 * `permission` is read on BOTH sides: packages/web decides what to render
 * with it, and packages/api gates each block of `/dashboard/summary` on
 * it. One declaration, so a widget cannot be shown by a client the server
 * would refuse — nor hidden from one it would serve.
 */
import { REGISTERED_MODULES } from "../modules/index.js";
import { composeWidgets } from "../modules/compose.js";
const BASE_DASHBOARD_WIDGETS = {
    /**
     * Low stock and overdue payment, each shown only above zero.
     *
     * Open, because its overdue half is served by the unpermissioned
     * endpoint. Its low-stock half needs `articles:read`, and gets it from
     * the article query being disabled without it: no permission means no
     * count rather than a zero standing in for one.
     */
    alerts: { key: "alerts", permission: null, layout: "panel" },
    "kpi-clients": {
        key: "kpi-clients",
        permission: "clients:read",
        layout: "kpi",
        planFeature: "clients",
    },
    "kpi-articles": {
        key: "kpi-articles",
        permission: "articles:read",
        layout: "kpi",
        planFeature: "articles",
    },
    "kpi-low-stock": {
        key: "kpi-low-stock",
        permission: "articles:read",
        layout: "kpi",
        planFeature: "articles",
    },
    "kpi-month-invoices": {
        key: "kpi-month-invoices",
        permission: null,
        layout: "kpi",
        planFeature: "invoices",
    },
    "quick-actions": { key: "quick-actions", permission: null, layout: "panel" },
    "recent-invoices": {
        key: "recent-invoices",
        permission: null,
        layout: "panel",
        planFeature: "invoices",
    },
    // The two breakdowns are read side by side, one against the other.
    "client-breakdown": {
        key: "client-breakdown",
        permission: "clients:read",
        layout: "half",
        planFeature: "clients",
    },
    "article-breakdown": {
        key: "article-breakdown",
        permission: "articles:read",
        layout: "half",
        planFeature: "articles",
    },
    // ── Ventes ──────────────────────────────────────────────────────
    // Company-wide sales figures answer to `invoices:read`. A till
    // operator holds only `invoices:read:own`, which does not satisfy
    // it — their own three blocks are below.
    "kpi-revenue": {
        key: "kpi-revenue",
        permission: "invoices:read",
        layout: "metric",
        planFeature: "invoices",
    },
    "kpi-receivables": {
        key: "kpi-receivables",
        permission: "invoices:read",
        layout: "metric",
        planFeature: "invoices",
    },
    "top-articles": {
        key: "top-articles",
        permission: "invoices:read",
        layout: "panel",
        planFeature: "invoices",
    },
    // ── Caisse ──────────────────────────────────────────────────────
    // Gated on `invoices:read:own` — the only invoice-read permission
    // any shipped role gives a cashier. The three blocks are
    // owner-scoped server-side, which is the condition under which the
    // `:own` grant may open them.
    "my-sales": {
        key: "my-sales",
        permission: "invoices:read:own",
        layout: "metric",
        planFeature: "pos",
    },
    "till-today": {
        key: "till-today",
        permission: "invoices:read:own",
        layout: "panel",
        planFeature: "pos",
    },
    "pending-tickets": {
        key: "pending-tickets",
        permission: "invoices:read:own",
        layout: "metric",
        planFeature: "pos",
    },
    // ── Stock ───────────────────────────────────────────────────────
    "stock-overview": {
        key: "stock-overview",
        permission: "stock:read",
        layout: "metric",
        planFeature: "stock",
    },
    "near-expiry": {
        key: "near-expiry",
        permission: "stock:read",
        layout: "panel",
        planFeature: "stock",
    },
    "stock-movements-today": {
        key: "stock-movements-today",
        permission: "stock:movements",
        layout: "metric",
        planFeature: "stock",
    },
    // ── Contrôle ────────────────────────────────────────────────────
    "verification-queue": {
        key: "verification-queue",
        permission: "invoices:verify",
        layout: "metric",
    },
    "mcf-doubt": {
        key: "mcf-doubt",
        permission: "mcf:arbitrer-signature",
        layout: "metric",
    },
    // ── Comptabilité ────────────────────────────────────────────────
    treasury: {
        key: "treasury",
        permission: "accounting:read",
        layout: "metric",
        planFeature: "accounting",
    },
    "kpi-margin": {
        key: "kpi-margin",
        permission: "accounting:read",
        layout: "metric",
        planFeature: "accounting",
    },
    "accounting-health": {
        key: "accounting-health",
        permission: "accounting:read",
        layout: "panel",
        planFeature: "accounting",
    },
    // ── Ressources humaines ─────────────────────────────────────────
    // `hr` and `payroll` are separate namespaces: a grant of `hr:*` does
    // not reach `payroll:read`, so the payroll block carries its own.
    "hr-headcount": { key: "hr-headcount", permission: "hr:read", layout: "metric", planFeature: "hr" },
    "hr-leave": { key: "hr-leave", permission: "hr:read", layout: "metric", planFeature: "hr" },
    "hr-contracts": { key: "hr-contracts", permission: "hr:read", layout: "metric", planFeature: "hr" },
    "payroll-status": {
        key: "payroll-status",
        permission: "payroll:read",
        layout: "panel",
        planFeature: "payroll",
    },
    // ── Exécution budgétaire ────────────────────────────────────────
    // `budget:read` is held by all four public-expenditure roles; the
    // acts they differ on are elsewhere, not on a figure.
    "budget-execution": {
        key: "budget-execution",
        permission: "budget:read",
        layout: "metric",
        planFeature: "budget",
    },
    "budget-queues": {
        key: "budget-queues",
        permission: "budget:read",
        layout: "panel",
        planFeature: "budget",
    },
    // ── Administration ──────────────────────────────────────────────
    establishments: {
        key: "establishments",
        permission: "establishments:read",
        layout: "metric",
    },
};
/**
 * The whole catalogue, in reading order.
 *
 * This is the I-RP2 fallback: a user whose roles declare no widget at all
 * — no role, an unknown code, or a role this registry does not know —
 * gets exactly this. It is the catalogue ENTIRE rather than a chosen
 * subset, because the fallback's job is to hide nothing: every key is
 * gated on its own permission at render and again server-side, so a
 * tenant's hand-made role sees precisely what it is allowed to see.
 *
 * Order is load-bearing in two places. The four original KPI cards stay
 * adjacent, so they share one four-column row; the two breakdowns stay
 * adjacent, so they pair up.
 */
/**
 * Core's own widgets plus those the modules contribute. A contributed widget
 * carries its own permission, so folding it in never widens access.
 */
export const DASHBOARD_WIDGETS = composeWidgets(BASE_DASHBOARD_WIDGETS, REGISTERED_MODULES);
export const DEFAULT_WIDGET_ORDER = [
    "alerts",
    "kpi-clients",
    "kpi-articles",
    "kpi-low-stock",
    "kpi-month-invoices",
    "kpi-revenue",
    "kpi-receivables",
    "kpi-margin",
    "treasury",
    "my-sales",
    "pending-tickets",
    "stock-overview",
    "stock-movements-today",
    "verification-queue",
    "mcf-doubt",
    "hr-headcount",
    "hr-leave",
    "hr-contracts",
    "budget-execution",
    "establishments",
    "quick-actions",
    "till-today",
    "recent-invoices",
    "top-articles",
    "near-expiry",
    "accounting-health",
    "payroll-status",
    "budget-queues",
    "client-breakdown",
    "article-breakdown",
];
export function getDashboardWidget(key) {
    return DASHBOARD_WIDGETS[key] ?? null;
}
//# sourceMappingURL=widgets.js.map